import type { FilteredListingControlsSnapshot } from './useFilteredListingControls';
import type { ListingUrlAdapter } from './useListingUrlState';

export type NumberRange = [number | null, number | null];
export type DateRange = [string | null, string | null];

export type ListingUrlCodec<T> = {
  parse: (values: string[]) => T | undefined;
  serialize: (value: T) => string[];
  equals: (a: T, b: T) => boolean;
};

const scalarCodec = <T>(
  parse: (value: string) => T | undefined,
  serialize: (value: T) => string,
): ListingUrlCodec<T> => ({
  parse: (values) => (values.length === 1 ? parse(values[0]) : undefined),
  serialize: (value) => [serialize(value)],
  equals: Object.is,
});

export const stringUrlCodec: ListingUrlCodec<string> = scalarCodec(
  (value) => value,
  (value) => value,
);

export const booleanUrlCodec: ListingUrlCodec<boolean> = scalarCodec(
  (value) => {
    if (value === 'true') return true;
    if (value === 'false') return false;
    return undefined;
  },
  String,
);

export const integerUrlCodec: ListingUrlCodec<number> = scalarCodec((value) => {
  if (!/^-?\d+$/.test(value)) return undefined;
  return Number.parseInt(value, 10);
}, String);

export const stringArrayUrlCodec: ListingUrlCodec<string[]> = {
  parse: (values) => values,
  serialize: (value) => value,
  equals: (a, b) =>
    a.length === b.length && a.every((value, index) => value === b[index]),
};

export function createEnumArrayUrlCodec<T extends string>(
  allowedValues: readonly T[],
): ListingUrlCodec<T[]> {
  const allowed = new Set<string>(allowedValues);
  return {
    parse: (values) =>
      values.every((value) => allowed.has(value))
        ? values.map((value) => value as T)
        : undefined,
    serialize: (value) => value,
    equals: (a, b) =>
      a.length === b.length && a.every((value, index) => value === b[index]),
  };
}

function rangeCodec<T>(
  parsePart: (value: string) => T | undefined,
  serializePart: (value: T) => string,
): ListingUrlCodec<[T | null, T | null]> {
  return {
    parse: (values) => {
      if (values.length !== 1) return undefined;
      const [fromValue, toValue, ...rest] = values[0].split('..');
      if (rest.length > 0) return undefined;
      const from = fromValue ? parsePart(fromValue) : null;
      const to = toValue ? parsePart(toValue) : null;
      if ((fromValue && from === undefined) || (toValue && to === undefined)) {
        return undefined;
      }
      return [from ?? null, to ?? null];
    },
    serialize: ([from, to]) => [
      `${from === null ? '' : serializePart(from)}..${
        to === null ? '' : serializePart(to)
      }`,
    ],
    equals: (a, b) => a[0] === b[0] && a[1] === b[1],
  };
}

export const numberRangeUrlCodec: ListingUrlCodec<NumberRange> = rangeCodec(
  (value) => {
    const number = Number(value);
    return Number.isFinite(number) ? number : undefined;
  },
  String,
);

export const dateRangeUrlCodec: ListingUrlCodec<DateRange> = rangeCodec(
  (value) => (/^\d{4}-\d{2}-\d{2}$/.test(value) ? value : undefined),
  (value) => value,
);

type TypedField<TFilters> = {
  [TKey in keyof TFilters]: {
    param: string;
    key: TKey;
    codec: ListingUrlCodec<TFilters[TKey]>;
  };
}[keyof TFilters];

type CreateTypedListingUrlAdapterOptions<TFilters> = {
  defaultFilters: TFilters;
  fields: TypedField<TFilters>[];
  qParam?: string;
  orderingParam?: string;
  defaultOrdering?: string;
};

export function createTypedListingUrlAdapter<TFilters extends object>({
  defaultFilters,
  fields,
  qParam = 'search',
  orderingParam = 'ordering',
  defaultOrdering,
}: CreateTypedListingUrlAdapterOptions<TFilters>): ListingUrlAdapter<
  FilteredListingControlsSnapshot<TFilters>
> {
  type Snapshot = FilteredListingControlsSnapshot<TFilters>;
  const managedKeys = [
    qParam,
    orderingParam,
    ...fields.map(({ param }) => param),
  ];

  return {
    hydrateFromSearch: (search) => {
      const params = new URLSearchParams(search.replace(/^\?/, ''));
      const partial: Partial<Snapshot> = {};
      if (params.has(qParam)) partial.q = params.get(qParam)!;
      if (params.has(orderingParam)) {
        partial.sort = params.get(orderingParam)!;
      }

      let hasFilters = false;
      const filters = { ...defaultFilters };
      fields.forEach(({ param, key, codec }) => {
        if (!params.has(param)) return;
        const value = codec.parse(params.getAll(param));
        if (value === undefined) return;
        filters[key] = value;
        hasFilters = true;
      });
      if (hasFilters) partial.filters = filters;
      return partial;
    },
    buildSearch: (existingSearch, snapshot) => {
      const params = new URLSearchParams(existingSearch.replace(/^\?/, ''));
      managedKeys.forEach((key) => params.delete(key));
      if (snapshot.q) params.set(qParam, snapshot.q);
      if (snapshot.sort && snapshot.sort !== defaultOrdering) {
        params.set(orderingParam, snapshot.sort);
      }
      fields.forEach(({ param, key, codec }) => {
        const value = snapshot.filters[key];
        if (codec.equals(value, defaultFilters[key])) return;
        codec
          .serialize(value)
          .forEach((encoded) => params.append(param, encoded));
      });
      return params.toString();
    },
  };
}
